import React, { useEffect, useState } from 'react';
import {
    Typography,
    Box,
    Paper,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    CleaningServices as CleanupIcon,
    Block as BlockIcon,
    Security as SecurityIcon,
    Assessment as StatsIcon,
    RestoreFromTrash as RestoreIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';

// Helper functie om runtime configuratie te gebruiken
const getCentralBackendURL = () => {
  // Runtime configuratie (Docker container)
  if (window.ENV_CONFIG && window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL) {
    return window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL;
  }
  // Build-time configuratie (fallback)
  return process.env.REACT_APP_CENTRAL_BACKEND_URL || 'http://localhost:8000';
};

const SystemAuditPage = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Audit Log state
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditPage, setAuditPage] = useState(0);
    const [auditRowsPerPage, setAuditRowsPerPage] = useState(25);
    const [auditTotal, setAuditTotal] = useState(0);
    
    // Rate Limit state
    const [rateLimits, setRateLimits] = useState([]);
    const [rateLimitPage, setRateLimitPage] = useState(0);
    const [rateLimitRowsPerPage, setRateLimitRowsPerPage] = useState(25);
    const [rateLimitTotal, setRateLimitTotal] = useState(0);
    const [rateLimitStats, setRateLimitStats] = useState(null);
    
    // Dialog state
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, type: null });
    const [cleanupDialog, setCleanupDialog] = useState({ open: false });
    const [resetIpDialog, setResetIpDialog] = useState({ open: false, ip: '' });
    const [cleanupHours, setCleanupHours] = useState(24);

    // Check if user is super admin
    const isSuperAdmin = user?.system_role === 'super_admin';

    // Helper function to get auth headers
    const getAuthHeaders = () => {
        const token = Cookies.get('auth_token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    };

    // Fetch audit logs
    const fetchAuditLogs = async () => {
        if (!isSuperAdmin) return;
        
        try {
            setLoading(true);
            const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/audit-logs?page=${auditPage + 1}&per_page=${auditRowsPerPage}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAuditLogs(data.logs || []);
                setAuditTotal(data.total || 0);
            } else {
                throw new Error('Failed to fetch audit logs');
            }
        } catch (err) {
            console.error('Error fetching audit logs:', err);
            setError('Fout bij het ophalen van audit logs');
        } finally {
            setLoading(false);
        }
    };

    // Fetch rate limits
    const fetchRateLimits = async () => {
        if (!isSuperAdmin) return;
        
        try {
            setLoading(true);
            const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/rate-limits?page=${rateLimitPage + 1}&per_page=${rateLimitRowsPerPage}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRateLimits(data.rate_limits || []);
                setRateLimitTotal(data.total || 0);
            } else {
                throw new Error('Failed to fetch rate limits');
            }
        } catch (err) {
            console.error('Error fetching rate limits:', err);
            setError('Fout bij het ophalen van rate limits');
        } finally {
            setLoading(false);
        }
    };

    // Fetch rate limit statistics
    const fetchRateLimitStats = async () => {
        if (!isSuperAdmin) return;
        
        try {
            const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/rate-limits/statistics`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRateLimitStats(data);
            } else {
                throw new Error('Failed to fetch rate limit statistics');
            }
        } catch (err) {
            console.error('Error fetching rate limit statistics:', err);
            setError('Fout bij het ophalen van rate limit statistieken');
        }
    };

    // Delete rate limit entry
    const deleteRateLimit = async (id) => {
        try {
            const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/rate-limits/${id}`, {
                method: 'DELETE',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                setSuccess('Rate limit entry succesvol verwijderd');
                await fetchRateLimits();
                await fetchRateLimitStats();
            } else {
                throw new Error('Failed to delete rate limit');
            }
        } catch (err) {
            console.error('Error deleting rate limit:', err);
            setError('Fout bij het verwijderen van rate limit');
        }
    };

    // Cleanup old rate limits
    const cleanupRateLimits = async () => {
        try {
            const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/rate-limits/cleanup`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ hours: cleanupHours })
            });

            if (response.ok) {
                const data = await response.json();
                setSuccess(`Cleanup voltooid: ${data.deleted_count} entries verwijderd`);
                await fetchRateLimits();
                await fetchRateLimitStats();
            } else {
                throw new Error('Failed to cleanup rate limits');
            }
        } catch (err) {
            console.error('Error cleaning up rate limits:', err);
            setError('Fout bij het opschonen van rate limits');
        }
    };

    // Reset IP rate limit
    const resetIpRateLimit = async () => {
        try {
            const response = await fetch(`${getCentralBackendURL()}/api/v1/admin/rate-limits/reset-ip`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ip_address: resetIpDialog.ip })
            });

            if (response.ok) {
                const data = await response.json();
                setSuccess(data.message);
                await fetchRateLimits();
                await fetchRateLimitStats();
            } else {
                throw new Error('Failed to reset IP rate limit');
            }
        } catch (err) {
            console.error('Error resetting IP rate limit:', err);
            setError('Fout bij het resetten van IP rate limit');
        }
    };

    // Effects
    useEffect(() => {
        if (isSuperAdmin) {
            if (tabValue === 0) {
                fetchAuditLogs();
            } else if (tabValue === 1) {
                fetchRateLimits();
                fetchRateLimitStats();
            }
        }
    }, [tabValue, auditPage, auditRowsPerPage, rateLimitPage, rateLimitRowsPerPage, isSuperAdmin]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Handle audit log pagination
    const handleAuditPageChange = (event, newPage) => {
        setAuditPage(newPage);
    };

    const handleAuditRowsPerPageChange = (event) => {
        setAuditRowsPerPage(parseInt(event.target.value, 10));
        setAuditPage(0);
    };

    // Handle rate limit pagination
    const handleRateLimitPageChange = (event, newPage) => {
        setRateLimitPage(newPage);
    };

    const handleRateLimitRowsPerPageChange = (event) => {
        setRateLimitRowsPerPage(parseInt(event.target.value, 10));
        setRateLimitPage(0);
    };

    // Format date
    const formatDate = (dateString) => {
        return moment(dateString).format('DD-MM-YYYY HH:mm:ss');
    };

    // Get action color
    const getActionColor = (action) => {
        if (action?.includes('success')) return 'success';
        if (action?.includes('failed')) return 'error';
        if (action?.includes('login')) return 'info';
        if (action?.includes('blocked')) return 'error';
        return 'default';
    };

    // Get rate limit status chip
    const getRateLimitStatusChip = (rateLimit) => {
        if (rateLimit.is_currently_blocked) {
            return <Chip label={`Geblokkeerd (${rateLimit.remaining_seconds}s)`} color="error" size="small" />;
        } else if (rateLimit.attempt_count > 10) {
            return <Chip label={`Hoog risico (${rateLimit.attempt_count})`} color="warning" size="small" />;
        } else if (rateLimit.attempt_count > 5) {
            return <Chip label={`Gemiddeld risico (${rateLimit.attempt_count})`} color="info" size="small" />;
        } else {
            return <Chip label={`Normaal (${rateLimit.attempt_count})`} color="success" size="small" />;
        }
    };

    // Show access denied if not super admin
    if (!isSuperAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    <Typography variant="h6">Toegang geweigerd</Typography>
                    <Typography>
                        Deze pagina is alleen beschikbaar voor super admin gebruikers.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Systeem Audit & Beveiliging
            </Typography>
            
            <Paper sx={{ mt: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Audit Logs" />
                    <Tab label="Rate Limiting & Brute Force" />
                </Tabs>

                {/* Audit Logs Tab */}
                {tabValue === 0 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Central Backend Audit Logs</Typography>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={fetchAuditLogs}
                                disabled={loading}
                            >
                                Vernieuwen
                            </Button>
                        </Box>

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tijdstip</TableCell>
                                        <TableCell>Gebruiker</TableCell>
                                        <TableCell>Actie</TableCell>
                                        <TableCell>Details</TableCell>
                                        <TableCell>IP Adres</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {auditLogs.map((log) => (
                                        <TableRow key={log.id}>
                                            <TableCell>{formatDate(log.created_at)}</TableCell>
                                            <TableCell>
                                                {log.user_email ? (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {log.user_name || 'Onbekend'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {log.user_email}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Systeem
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={log.action}
                                                    color={getActionColor(log.action)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                                                    {log.details || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {log.ip_address || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={auditTotal}
                            rowsPerPage={auditRowsPerPage}
                            page={auditPage}
                            onPageChange={handleAuditPageChange}
                            onRowsPerPageChange={handleAuditRowsPerPageChange}
                        />
                    </Box>
                )}

                {/* Rate Limiting Tab */}
                {tabValue === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Rate Limiting & Brute Force Bescherming</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<StatsIcon />}
                                    onClick={fetchRateLimitStats}
                                    disabled={loading}
                                >
                                    Statistieken
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<CleanupIcon />}
                                    onClick={() => setCleanupDialog({ open: true })}
                                    disabled={loading}
                                >
                                    Opschonen
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RestoreIcon />}
                                    onClick={() => setResetIpDialog({ open: true, ip: '' })}
                                    disabled={loading}
                                >
                                    IP Resetten
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchRateLimits}
                                    disabled={loading}
                                >
                                    Vernieuwen
                                </Button>
                            </Box>
                        </Box>

                        {/* Statistics Cards */}
                        {rateLimitStats && (
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="primary">
                                                {rateLimitStats.total_statistics.total_ip_entries}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Totaal IP's Getracked
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="error">
                                                {rateLimitStats.total_statistics.currently_blocked}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Momenteel Geblokkeerd
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="info">
                                                {rateLimitStats.total_statistics.recent_activity_24h}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Activiteit (24u)
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="warning">
                                                {rateLimitStats.auto_degradation_candidates.total_pending_degradations}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Pending Auto-Degradatie
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>IP Adres</TableCell>
                                        <TableCell>Endpoint</TableCell>
                                        <TableCell>Pogingen</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Eerste Poging</TableCell>
                                        <TableCell>Laatste Poging</TableCell>
                                        <TableCell>Laatste Email</TableCell>
                                        <TableCell>Acties</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rateLimits.map((rateLimit) => (
                                        <TableRow key={rateLimit.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {rateLimit.ip_address}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={rateLimit.endpoint_type} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {rateLimit.attempt_count}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {getRateLimitStatusChip(rateLimit)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {rateLimit.first_attempt ? formatDate(rateLimit.first_attempt) : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {rateLimit.last_attempt ? formatDate(rateLimit.last_attempt) : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {rateLimit.last_email || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Verwijder rate limit entry">
                                                    <IconButton
                                                        onClick={() => setDeleteDialog({ open: true, id: rateLimit.id, type: 'rate_limit' })}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={rateLimitTotal}
                            rowsPerPage={rateLimitRowsPerPage}
                            page={rateLimitPage}
                            onPageChange={handleRateLimitPageChange}
                            onRowsPerPageChange={handleRateLimitRowsPerPageChange}
                        />
                    </Box>
                )}
            </Paper>

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null, type: null })}
            >
                <DialogTitle>Bevestiging</DialogTitle>
                <DialogContent>
                    <Typography>
                        Weet je zeker dat je deze rate limit entry wilt verwijderen? 
                        Dit zal het IP adres deblokkeren als het geblokkeerd is.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, id: null, type: null })}>
                        Annuleren
                    </Button>
                    <Button
                        onClick={() => {
                            if (deleteDialog.type === 'rate_limit') {
                                deleteRateLimit(deleteDialog.id);
                            }
                            setDeleteDialog({ open: false, id: null, type: null });
                        }}
                        color="error"
                        variant="contained"
                    >
                        Verwijderen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cleanup Dialog */}
            <Dialog
                open={cleanupDialog.open}
                onClose={() => setCleanupDialog({ open: false })}
            >
                <DialogTitle>Rate Limits Opschonen</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Dit verwijdert alle rate limit entries die ouder zijn dan het opgegeven aantal uren 
                        en niet meer geblokkeerd zijn.
                    </Typography>
                    <TextField
                        label="Uren (standaard: 24)"
                        type="number"
                        value={cleanupHours}
                        onChange={(e) => setCleanupHours(parseInt(e.target.value) || 24)}
                        fullWidth
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCleanupDialog({ open: false })}>
                        Annuleren
                    </Button>
                    <Button
                        onClick={() => {
                            cleanupRateLimits();
                            setCleanupDialog({ open: false });
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Opschonen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset IP Dialog */}
            <Dialog
                open={resetIpDialog.open}
                onClose={() => setResetIpDialog({ open: false, ip: '' })}
            >
                <DialogTitle>IP Rate Limit Resetten</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Voer het IP adres in dat je wilt resetten:
                    </Typography>
                    <TextField
                        label="IP Adres"
                        value={resetIpDialog.ip}
                        onChange={(e) => setResetIpDialog({ ...resetIpDialog, ip: e.target.value })}
                        fullWidth
                        margin="normal"
                        placeholder="bijv. 192.168.1.100"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetIpDialog({ open: false, ip: '' })}>
                        Annuleren
                    </Button>
                    <Button
                        onClick={() => {
                            resetIpRateLimit();
                            setResetIpDialog({ open: false, ip: '' });
                        }}
                        color="primary"
                        variant="contained"
                        disabled={!resetIpDialog.ip}
                    >
                        Resetten
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            {/* Success Snackbar */}
            <Snackbar
                open={!!success}
                autoHideDuration={4000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SystemAuditPage; 